import {
  Header,
  QuestionnaireFormProps,
  QuestionnaireQuestions,
} from '@getpopsure/qnr-framework';
import { Button, illustrations } from '@popsure/dirty-swan';
import { NavButton } from 'components/NavButton';
import routes from 'constants/routes';
import { useEffect } from 'react';
import { generatePath, useHistory, useParams } from 'react-router';
import { useSafeTranslation } from 'shared/i18n';

import { IconCard } from '../../../../components/IconCard';
import styles from './style.module.scss';

export const PetHealthGettingStarted = <
  Questionnaire extends QuestionnaireQuestions
>({
  onSubmitValue,
  setValidForSubmission,
}: QuestionnaireFormProps<Questionnaire, boolean>) => {
  const { policyId } = useParams<{ policyId: string }>();
  const { location } = useHistory<{ from?: string }>();

  const { t } = useSafeTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
    setValidForSubmission(true);
  }, [setValidForSubmission]);

  const handleSubmit = () => {
    onSubmitValue(true);
  };

  return (
    <div className={`mb80 ${styles.animatedContainer}`}>
      <Header onSubmit={handleSubmit}>
        <div className="p-body">
          <div className="w100 ai-center d-flex">
            <NavButton
              title={t(
                'claims.petHealth.gettingStarted.navButton.title',
                'Policy'
              )}
              path={
                location.state?.from !== 'policy'
                  ? routes.claims.create.path
                  : generatePath(routes.me.policies.detail.path, { policyId })
              }
            />
          </div>
          <div>
            <div className="my16">
              <h1 className="p-h1">
                {t('claims.petHealth.gettingStarted.title', 'Getting started')}
              </h1>
              <p className="p-p mt16">
                {t(
                  'claims.petHealth.gettingStarted.description',
                  'Here are a few useful tips to consider when making a claim:'
                )}
              </p>
            </div>
            <div className={styles.cardContainer}>
              <IconCard
                title={t(
                  'claims.petHealth.gettingStarted.medicalQuestionnaire.title',
                  'Check invoice details'
                )}
                iconSrc={illustrations.checkDoc}
                iconAlt="check document"
                iconWidth={48}
                className="bs-sm bg-white px24 py24 br8 wmx8"
              >
                {t(
                  'claims.petHealth.gettingStarted.medicalQuestionnaire.description',
                  'The invoice must include the detailed breakdown of the costs, pet’s name and chip number and specify if the procedure is surgery or surgery-related.'
                )}
              </IconCard>
              <IconCard
                title={t(
                  'claims.petHealth.gettingStarted.payInvoice.title',
                  'Pay your invoice as soon as possible'
                )}
                iconSrc={illustrations.creditCard}
                iconAlt="credit card"
                iconWidth={48}
                className="bs-sm bg-white px24 py24 br8 wmx8"
              >
                {t(
                  'claims.petHealth.gettingStarted.payInvoice.description',
                  'We recommend that you pay the invoice quickly to avoid late fees while we review your claim. You can request an extension from your medical professional if needed.'
                )}
              </IconCard>
            </div>
          </div>
          <Button
            className="wmx5 wmn3 mt24 mr16 mb8"
            onClick={() => setValidForSubmission(true)}
            data-cy="getting-started-button"
          >
            {t('claims.petHealth.gettingStarted.button.text', 'Get Started')}
          </Button>
        </div>
      </Header>
    </div>
  );
};
